import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { TextInput } from '../../../components/TextInput';
import type { BillingInvoice } from '../../../types/adminBilling';
import { formatMoney } from './billingFormat';

interface VoidInvoiceModalProps {
  invoice: BillingInvoice | null;
  saving: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Void one invoice.
 *
 * The reason is optional on the wire but asked for here, because a voided invoice with no reason
 * is a hole in the record that whoever looks at this organization next has to guess at. It is
 * stored, shown in the table afterwards, and is the only explanation anyone will have.
 */
export const VoidInvoiceModal = ({
  invoice,
  saving,
  error,
  onConfirm,
  onClose,
}: VoidInvoiceModalProps) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (invoice) setReason('');
  }, [invoice]);

  return (
    <ConfirmModal
      isOpen={invoice !== null}
      title="Void invoice"
      message={
        invoice
          ? `Void the ${formatMoney(
              invoice.amountCents,
              invoice.currency
            )} invoice? It stays on the record as voided and stops counting towards what this organization owes.`
          : ''
      }
      confirmText={saving ? 'Voiding...' : 'Void invoice'}
      busy={saving}
      onConfirm={() => onConfirm(reason)}
      onClose={onClose}
    >
      <div className="billing-form">
        <TextInput
          label="Reason (optional)"
          placeholder="Raised in error"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={saving}
          data-testid="billing-void-reason"
        />
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-void-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
